package ui

import io.kvision.core.*
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.utils.perc
import io.kvision.utils.px
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import structs.Commander

/**
 * Step-3 content of [CommanderSelectionDialog] when SIMULATION is selected.
 *
 * The page collects three pieces of configuration the dialog then forwards
 * to [CommanderSelectionDialog.onConfirmSelection] via the [onPlayClicked]
 * callback:
 *  - `names`: the human-owned roster (1..4 entries, all distinct)
 *  - `slots`: total slot count (2..4)
 *  - `mapPath`: the bundled map the game should boot on
 *
 * The Play button stays disabled until the validation gate is satisfied:
 * `roster > 0 && 2 ≤ slots ≤ 4 && slots ≥ roster.size && mapPath.isNotBlank()`.
 *
 * **Partition of responsibility:** this widget does NOT talk to RPCs or to
 * MatchmakingClient. Its sole output is the callback. Tasks 9 / 10 / 10.5
 * own the downstream wiring (GameRequest fields + RPC + GameInit consumption).
 *
 * @param savedCommanders the user's cached commanders (from
 *                        `World.availableCommanders` + `CommanderCache`). The
 *                        page renders one toggleable card per entry.
 * @param onPlayClicked   invoked exactly once per Play click, with the
 *                        validated payload. The dialog forwards this to its
 *                        own `onConfirmSelection`.
 */
class SimulationSettingsPage(
    private val savedCommanders: List<Commander>,
    private val onPlayClicked: (humanNames: List<String>, totalSlots: Int, mapPath: String) -> Unit,
    /**
     * Single-Play contract 2026-08-08: invoked on every roster / map / slot
     * change so the dialog footer Play can re-evaluate its `disabled`
     * property. Default no-op preserves binary-compat with any external
     * callers that don't care about the gate.
     */
    private val onStateChanged: () -> Unit = {}
) : SimplePanel(className = "simulation-settings-page")
{
    private var selectedNames: List<String> = emptyList()
    private var totalSlotCount: Int = 2
    private var selectedMapPath: String = ""

    private var mapPicker: SimulationMapPicker? = null

    /**
     * The section vPanel that owns the map picker. Tracked so
     * [rebuildMapPicker] can remove the old picker from THIS panel
     * (not from the outer [SimulationSettingsPage], where the picker
     * is not actually a direct child) and add the new picker back
     * into the same container. Without this anchor, every
     * `simulateSlotCountChange` left the previous picker orphaned
     * inside its real parent while stacking fresh pickers as
     * direct children of the page — producing the "all the map
     * cards collapsed to width 30 and stacked above the new full
     * width picker" symptom visible after a single +/- click.
     */
    private var mapPickerContainer: io.kvision.panel.VPanel? = null

    /**
     * Live read-out of [totalSlotCount] shown between the - / +
     * buttons. Captured so [simulateSlotCountChange] can update the
     * displayed text without rebuilding the slot row — the original
     * `p("$totalSlotCount") { ... }` literal only embedded the
     * initial value (2) once in init, so the count display stayed at
     * 2 even after the user clicked + and the picker rebuilt with
     * the 3-4 player maps.
     */
    private lateinit var slotCountDisplay: io.kvision.html.P

    private lateinit var searchField: io.kvision.form.text.TextInput
    private lateinit var noRosterMatches: SimplePanel
    private val commanderMiniCardEntries = mutableListOf<CommanderMiniCardEntry>()
    private val miniCardsByName = mutableMapOf<String, CommanderMiniCard>()

    init
    {
        width = 100.perc
        flexGrow = 1

        Logger.info(
            LogCategory.UI,
            "SimulationSettingsPage: mounted with ${savedCommanders.size} saved commanders"
        )

        // Scrollable body container — mirrors .commander-selection-list from
        // Step 1 (CSS line 321-326). The footer (Cancel/Back/Play) lives in
        // the dialog chrome above; the body scrolls internally so the user
        // can always reach the footer Play button even with many commanders
        // or many map options.
        vPanel(spacing = 16, className = "simulation-settings-body") {
            flexGrow = 1
            flexShrink = 1
            minHeight = 0.px
            overflowY = Overflow.AUTO

            // === Section 1: Roster ===
            vPanel(spacing = 8) {
                width = 100.perc

                h4("Roster") {
                    textAlign = TextAlign.CENTER
                    fontSize = CssSize(20, UNIT.px)
                    color = Color("#f4f6fb")
                    marginBottom = 4.px
                }

                p("Pick the commanders you want to control (1-4):") {
                    fontSize = CssSize(14, UNIT.px)
                    color = Color("#a0a8cc")
                    textAlign = TextAlign.CENTER
                    margin = CssSize(0, UNIT.px)
                }

                // Search field — same UX as Step 1's commander search,
                // wrapped in a `.collection-search` hPanel with a
                // magnifying-glass icon prefix for visual coherence.
                hPanel(
                    className = "collection-search",
                    spacing = 12,
                    alignItems = io.kvision.core.AlignItems.CENTER
                ) {
                    width = 100.perc
                    icon("fas fa-search")
                    searchField = textInput {
                        width = 100.perc
                        placeholder = "Search commanders..."
                        onInput {
                            applyCommanderFilter()
                        }
                        setAttribute("spellcheck", "false")
                    }
                }

                // Commander toggles — each one is a mini-card.
                savedCommanders.forEach { commander ->
                    val card = CommanderMiniCard(commander) { miniCard ->
                        toggleCommander(commander.name, miniCard)
                    }
                    commanderMiniCardEntries.add(
                        CommanderMiniCardEntry(commander.name.lowercase(), card.cardPanel)
                    )
                    miniCardsByName[commander.name.lowercase()] = card
                    add(card.cardPanel)
                }

                noRosterMatches = SimplePanel {
                    p("No commanders match that search.") {
                        color = Color("#a0a8cc")
                        textAlign = TextAlign.CENTER
                        fontSize = CssSize(14, UNIT.px)
                        margin = CssSize(0, UNIT.px)
                    }
                }
                noRosterMatches.visible = false
                noRosterMatches.display = Display.NONE
                add(noRosterMatches)
            }

            // === Section 2: Total Slots ===
            vPanel(spacing = 8) {
                width = 100.perc

                h4("Total Slots") {
                    textAlign = TextAlign.CENTER
                    fontSize = CssSize(20, UNIT.px)
                    color = Color("#f4f6fb")
                    marginBottom = 4.px
                }

                hPanel(
                    spacing = 16,
                    justify = io.kvision.core.JustifyContent.CENTER,
                    alignItems = io.kvision.core.AlignItems.CENTER
                ) {
                    width = 100.perc

                    button("-") {
                        width = CssSize(60, UNIT.px)
                        height = CssSize(40, UNIT.px)
                        onClick {
                            simulateSlotCountChange(totalSlotCount - 1)
                        }
                    }

                    slotCountDisplay = p("$totalSlotCount") {
                        fontSize = CssSize(20, UNIT.px)
                        color = Color("#f4f6fb")
                        margin = CssSize(0, UNIT.px)
                        width = CssSize(40, UNIT.px)
                        textAlign = TextAlign.CENTER
                    }

                    button("+") {
                        width = CssSize(60, UNIT.px)
                        height = CssSize(40, UNIT.px)
                        onClick {
                            simulateSlotCountChange(totalSlotCount + 1)
                        }
                    }
                }
            }

            // === Section 3: Map ===
            mapPickerContainer = vPanel(spacing = 8) {
                width = 100.perc

                mapPicker = SimulationMapPicker(slotCount = totalSlotCount) { mapPath ->
                    simulateMapSelection(mapPath)
                }
                add(mapPicker!!)
            }
            add(mapPickerContainer!!)

            // === Footer Play button was removed 2026-08-08 (single-Play contract) ===
            // The dialog's outer Play reads isReadyForConfirm() + currentPayload()
            // from this page on click. No inner button renders anymore.
        }

        recomputePlayButton()
    }

    /**
     * Toggles [commanderName] in the roster. If currently selected, remove it;
     * otherwise add it. Validation runs after every toggle so the dialog
     * footer Play reflects the latest gate state.
     */
    private fun toggleCommander(commanderName: String, miniCard: CommanderMiniCard)
    {
        val isAdding = commanderName !in selectedNames
        selectedNames = if (commanderName in selectedNames)
        {
            Logger.info(LogCategory.UI, "SimulationSettingsPage: commander toggle '$commanderName' removed")
            miniCard.setActive(false)
            selectedNames - commanderName
        }
        else
        {
            Logger.info(LogCategory.UI, "SimulationSettingsPage: commander toggle '$commanderName' added")
            miniCard.setActive(true)
            selectedNames + commanderName
        }
        recomputePlayButton()
        onStateChanged()
        @Suppress("UNUSED_VARIABLE")
        val unused = isAdding
    }

    /**
     * Filters the visible commander mini-cards by case-insensitive substring
     * match on the search field. Mirrors the Step 1
     * [CommanderSelectionDialog.applyCommanderFilter] pattern. Shows the
     * "No commanders match that search" empty state when no cards match.
     */
    private fun applyCommanderFilter()
    {
        if (!::searchField.isInitialized || !::noRosterMatches.isInitialized)
        {
            return
        }
        val filterValue = searchField.value?.trim()?.lowercase().orEmpty()
        var matchCount = 0
        commanderMiniCardEntries.forEach { entry ->
            val matches = filterValue.isBlank() || entry.name.contains(filterValue)
            entry.panel.display = if (matches) Display.FLEX else Display.NONE
            entry.panel.visible = matches
            if (matches)
            {
                matchCount++
            }
        }
        val shouldShowEmptyState = matchCount == 0
        noRosterMatches.visible = shouldShowEmptyState
        noRosterMatches.display = if (shouldShowEmptyState) Display.FLEX else Display.NONE
    }

    private data class CommanderMiniCardEntry(
        val name: String,
        val panel: SimplePanel
    )

    fun simulateMapSelection(mapPath: String)
    {
        Logger.info(LogCategory.UI, "SimulationSettingsPage: map selected '$mapPath'")
        selectedMapPath = mapPath
        recomputePlayButton()
        onStateChanged()
    }

    /**
     * Clamps [count] into 2..4 and recomputes the play-gate. When the slot
     * count crosses the 2/3 boundary, the map inventory changes — we
     * rebuild the picker in-place so the user sees the new options without
     * a remount.
     */
    fun simulateSlotCountChange(count: Int)
    {
        val clamped = count.coerceIn(2, 4)
        if (clamped == totalSlotCount) return
        Logger.info(LogCategory.UI, "SimulationSettingsPage: slotCount = $clamped")
        totalSlotCount = clamped
        if (::slotCountDisplay.isInitialized)
        {
            slotCountDisplay.content = clamped.toString()
        }
        // Map picker rebuild — clear stale map selection if it no longer fits.
        if (selectedMapPath.isNotBlank() && selectedMapPath !in availableMaps())
        {
            selectedMapPath = ""
        }
        rebuildMapPicker()
        recomputePlayButton()
        onStateChanged()
    }

    private fun rebuildMapPicker()
    {
        val container = mapPickerContainer ?: return
        // Remove the old picker from its REAL parent container (the
        // section vPanel), not from `this` page. The picker is a
        // grand-child of `this`, so `this.remove(picker)` is a no-op
        // and the orphaned DOM kept rendering as width-collapsed
        // ghost cards every time the slot count crossed the 2/3
        // boundary.
        mapPicker?.let { container.remove(it) }
        mapPicker = SimulationMapPicker(slotCount = totalSlotCount) { mapPath ->
            simulateMapSelection(mapPath)
        }
        mapPicker?.let { container.add(it) }
    }

    /**
     * Read-only view of the page's current configuration. The dialog
     * footer Play reads this on click so it doesn't need a callback push
     * from a separate inner button.
     *
     * @return Triple of (roster, totalSlotCount, mapPath) reflecting the
     *         user's selections on the page right now. Fields may be empty
     *         / blank if the user has not configured yet.
     */
    fun currentPayload(): Triple<List<String>, Int, String> =
        Triple(selectedNames, totalSlotCount, selectedMapPath)

    /**
     * Pure-logic gate. Returns true iff the current configuration is
     * complete enough to dispatch the simulation. The dialog footer Play
     * reads this on every change so its `disabled` property stays in
     * lockstep with the page's validation.
     */
    fun isReadyForConfirm(): Boolean =
        selectedNames.isNotEmpty()
            && selectedNames.size <= totalSlotCount
            && totalSlotCount in 2..4
            && selectedMapPath.isNotBlank()

    /**
     * The Play-button gate. Kept as a single source of truth so the test
     * seam and the button's `disabled` property cannot drift.
     */
    private fun recomputePlayButton()
    {
        val valid = isReadyForConfirm()
        Logger.debug(
            LogCategory.SYSTEM,
            "SimulationSettingsPage: page gate valid=$valid (roster=${selectedNames.size}/$totalSlotCount map='$selectedMapPath')"
        )
    }

    private fun availableMaps(): List<String> = mapPicker?.availableMaps() ?: emptyList()

    // === Test seams ===
    // The jsBrowserTest suite drives these directly to avoid the cost of
    // a real KVision event loop per assertion.

    fun simulateCommanderToggle(name: String): Boolean
    {
        // Drive the same path the production click handler uses — finds
        // the matching mini-card and toggles via the production code.
        // Returns false when the name doesn't match a known mini-card
        // (test seam calls may reference commanders not in the dialog's
        // commander list).
        val miniCard = miniCardsByName[name.lowercase()] ?: return false
        toggleCommander(name, miniCard)
        return true
    }

    fun playButtonEnabledForTest(): Boolean = isReadyForConfirm()

    fun availableMapsForTest(): List<String> = mapPicker?.availableMaps() ?: emptyList()

    /**
     * Test seam: types into the search field and triggers the onInput
     * handler so the filter logic runs. Mirrors what a real user would
     * produce by typing in the input.
     */
    fun simulateSearchText(text: String)
    {
        if (!::searchField.isInitialized) return
        searchField.value = text
        applyCommanderFilter()
    }

    /**
     * Test seam: counts how many commander mini-cards are currently
     * visible (display=FLEX). Used by tests to verify the search field
     * actually filters the list.
     */
    fun simulateRosterCountForTest(): Int =
        commanderMiniCardEntries.count { entry ->
            entry.panel.visible && entry.panel.display != Display.NONE
        }

    /**
     * Test seam: returns the underlying TextInput so tests can drive it
     * directly (e.g. via `setSearchText` if KVision exposes it).
     */
    fun searchFieldForTest(): TextInput? =
        if (::searchField.isInitialized) searchField else null

    /**
     * Returns a synthetic structural summary used by jsBrowserTest assertions.
     * Rather than scraping the live DOM (which would require a real
     * browser-side element handle), we expose the same labels + selected
     * state as a string the test can `contains`-check. This decouples the
     * contract from KVision's DOM rendering — the test pins what the page
     * LOGICALLY renders, not how it's rendered in pixels.
     */
    fun renderForTest(): String
    {
        val sb = StringBuilder()
        sb.append("Roster:")
        savedCommanders.forEach { sb.append(" ").append(it.name) }
        sb.append("|Selected:").append(selectedNames.joinToString(","))
        sb.append("|TotalSlots:").append(totalSlotCount)
        sb.append("|Map:").append(selectedMapPath.ifBlank { "<none>" })
        sb.append("|AvailableMaps:").append(availableMaps().joinToString(","))
        sb.append("|Gate:").append(if (playButtonEnabledForTest()) "open" else "closed")
        sb.append("|SimulationSettings:scroll-body")
        sb.append("|SimulationSettings:search-field")
        return sb.toString()
    }
}

/**
 * Compact commander toggle card for the Simulation Settings page.
 *
 * Uses the same `.commander-selection-card` styling as Step 1's full
 * commander card but with a smaller footprint — no description block,
 * no empire/type/trait metadata. Just the commander name + an
 * active-state glow when toggled into the roster.
 *
 * Single-Play contract 2026-08-08: clicking the mini-card toggles the
 * commander's inclusion in the simulation roster. The dialog footer
 * Play's `disabled` property is driven by `isReadyForConfirm()`, which
 * the page recomputes after every toggle.
 */
private class CommanderMiniCard(
    private val commander: structs.Commander,
    private val onToggle: (CommanderMiniCard) -> Unit
)
{
    val cardPanel: SimplePanel
    private var active = false

    init
    {
        cardPanel = SimplePanel()
        cardPanel.addCssClass("commander-selection-card")
        cardPanel.addCssClass("commander-mini-card")
        cardPanel.padding = CssSize(12, UNIT.px)
        cardPanel.borderRadius = 8.px
        cardPanel.border = Border(1.px, BorderStyle.SOLID, Color("rgba(255, 255, 255, 0.08)"))
        cardPanel.cursor = Cursor.POINTER

        cardPanel.vPanel(spacing = 4) {
            hPanel(spacing = 10, alignItems = io.kvision.core.AlignItems.CENTER) {
                icon("fas fa-mountain") {
                    fontSize = io.kvision.core.CssSize(20, UNIT.px)
                    color = Color("#7a86e8") // Step 1's purple-blue accent
                }
                vPanel(spacing = 2) {
                    span(commander.name.ifBlank { "Unnamed Commander" }) {
                        fontSize = io.kvision.core.CssSize(18, UNIT.px)
                        fontWeight = io.kvision.core.FontWeight.BOLD
                        color = Color("#ffffff")
                    }
                    span("Click to add/remove from your simulation roster") {
                        fontSize = io.kvision.core.CssSize(12, UNIT.px)
                        color = Color("#cbd3ff")
                    }
                }
            }
        }

        cardPanel.onClick {
            onToggle(this@CommanderMiniCard)
        }
    }

    fun setActive(isActive: Boolean)
    {
        active = isActive
        if (isActive)
        {
            cardPanel.addCssClass("commander-selection-card-active")
        }
        else
        {
            cardPanel.removeCssClass("commander-selection-card-active")
        }
    }

    fun isActiveForTest(): Boolean = active
}
